import { Component, computed, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Role } from 'src/app/models/user/role.model';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { UsersService } from 'src/app/services/users.service';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { AdministratorService } from 'src/app/services/administrator.service';
import { RightSidebarContainerComponent } from '../../components/right-sidebar-container/right-sidebar-container.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { BtnCellRenderer } from '../../components/btn-cell-renderer/btn-cell-renderer.component';
import { AgGridWrapperComponent } from '../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { TextTooltipRenderer } from '../../components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from '../../utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { SearchSortComponent } from '../../components/search-sort/search-sort.component';
import { FormStatusCellRenderer } from '../../components/form-status/form-status-cell-renderer.component';
import { StatesService } from 'src/app/services/states.service';
import { formatPhone } from '../../utils/phone.util';
import { tenDigitPhoneValidator } from '../../validators/phone.validator';
import { PhoneInputDirective } from 'src/app/modules/shared/directives/phone-input.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';


@Component({
  selector: 'app-administrator-list',
  templateUrl: './administrator-list.component.html',
  styleUrls: ['./administrator-list.component.scss'],
  imports: [
    MatSidenavModule,
    RightSidebarContainerComponent,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatPaginatorModule,
    MatSelectModule,
    AgGridWrapperComponent,
    PhoneInputDirective,
    SearchSortComponent,
    MatCheckboxModule,
],
  standalone: true,
})
export class AdministratorListComponent implements OnInit, OnDestroy {
  isLoading: boolean;
  public rowData$!: any[];
  @ViewChild('drawer') drawer: MatDrawer;
  isEditing = false;
  showAll: boolean = false;
  id: number;

  public form: FormGroup = new FormGroup({
    name: new FormControl('', {
      validators: [Validators.required],
      updateOn: 'blur',
    }),
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
    }),
    phoneNumber: new FormControl('', {
      validators: [
        Validators.required,
        tenDigitPhoneValidator(),
      ],
        updateOn: 'blur'  
    }),
    mailingStreetAddress: new FormControl('', { validators: [Validators.required] }),
    mailingStreetAddressLine2: new FormControl(''),
    mailingCity: new FormControl('', { validators: [Validators.required] }),
    mailingStateId: new FormControl('', { validators: [Validators.required] }),
    mailingZipCode: new FormControl('', {validators: [Validators.required]})  
  });

  statesList: any[] = [];
  subscriptions: Subscription[] = [];

  public columnDefs: ColDef[] = [
    {
      ...baseColDef,
      field: 'name',
      headerName: 'Name',
      flex: 2,
      cellRenderer: TextTooltipRenderer,
    },
    {
      ...baseColDef,
      field: 'email',
      headerName: 'Email',
      cellRenderer: TextTooltipRenderer,
      flex: 2
    },
    {
      ...baseColDef,
      field: 'phoneNumber',
      headerName: 'Phone Number',
      cellRenderer: TextTooltipRenderer,
      valueFormatter: (params) => {
        return formatPhone(params.value);
      },
      flex: 2
    },
    {
      ...baseColDef,
      field: 'physicalAddress',
      headerName: 'Address',
      cellRenderer: TextTooltipRenderer,
      valueGetter: (params: ValueGetterParams) => {
        const data = params.data;
        if (!data) return '';
        
        const parts = [];
        if (data.mailingStreetAddress) parts.push(data.mailingStreetAddress);
        if (data.mailingStreetAddressLine2) parts.push(data.mailingStreetAddressLine2);
        if (data.mailingCity) parts.push(data.mailingCity);
        if (data.mailingStateName) parts.push(data.mailingStateName);
        if (data.mailingZipCode) parts.push(data.mailingZipCode);
        
        return parts.length > 0 ? parts.join(', ') : '';
      },
      flex: 3
    },
    {
      ...baseColDef,
      field: 'isActive',
      valueGetter: (params) => {
        return params?.data?.isActive ? {
          statusId: 5,
          name: 'Enabled'
        }: {
          statusId: 6,
          name: 'Disabled'
        }
      },
      headerName: 'Status',
      width: 120,
      cellRenderer: FormStatusCellRenderer,
      comparator: (a, b) => (a?.statusId ?? 0) - (b?.statusId ?? 0),
    },
  ];

  pageSettings: {
    defaultPageSize: number,
    pageSizeOptions: number[],
  } = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50 ,100]
  }
  currentPage: number = 0;
  pageSize: number = this.pageSettings.defaultPageSize;
  searchControl: FormControl = new FormControl('');
  currentSortField = {
    sortValue: 'id',
  };
  currentIsAscending = true;
  totalCount: number = 0;
  searchKey: string = '';

  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({
      sortValue: this.currentSortField.sortValue,
    }),
    isAscending: new FormControl(this.currentIsAscending),
  });
  sortOptions = [
    { label: 'Name', sortValue: 'name' },
  ];
  gridApi: GridApi;

  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin || this.userRole() === Role.SystemUser);
  readonly isProvider= computed(() => this.userRole() === Role.Provider);
  readonly isSystem = computed(() => this.userRole() === Role.SystemUser);

  constructor(
    private service: AdministratorService,
    private userService: UsersService,
    private deleteDialog: DeleteConfirmationDialogService,
    private statesService: StatesService,
  ) {}

  ngOnInit() {
    const statesSubscription = this.statesService.get().subscribe((data: any) => {
      if (!data.isError) {
        this.statesList = data.data;
      }
    });
    this.subscriptions.push(statesSubscription);

    const searchSubscription =this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.currentPage = 0;
          this.reload();
        }),
      )
      .subscribe();
    this.subscriptions.push(searchSubscription);
  }

  openRightSidebar(data: any = null) {
    if (data) {
      this.id = data.id;
      this.form.setValue({
        name: data.name,
        email: data.email || '',
        phoneNumber: data.phoneNumber || '',
        mailingStreetAddress: data.mailingStreetAddress || '',
        mailingStreetAddressLine2: data.mailingStreetAddressLine2 || '',
        mailingCity: data.mailingCity || '',
        mailingStateId: data.mailingStateId || '',
        mailingZipCode: data.mailingZipCode || '',
      });

      this.isEditing = true;
    } else {
      this.form.reset();
      this.isEditing = false;
    }
    this.drawer.toggle();
  }

  onConfirmation(result: any) {
    if (result.confirmed) {
      this.service.delete(result.id).subscribe(() => {
        this.reload();
      });
    }
  }

  close() {
    this.form.reset();
    this.drawer.close();
  }

  onSubmit() {
    if (this.form.valid) {
      this.isLoading = true;
      const data = this.form.value;

      if (this.isEditing) {
        this.service.update(this.id.toString(), data).subscribe((data) => {
          this.reload();
        });
      } else {
        this.service.add(this.form.value).subscribe((data) => {
          this.reload();
        });
      }
    }
  }

  onChangeAvailability(data: any) {
    var deleteSuscription = this.deleteDialog
      .openDeleteConfirmation(
        `This administrator will be ${data.isActive ? 'disabled' : 'enabled'}.`,
        data.isActive ? 'Disable confirmation' : 'Enable confirmation',
        data.isActive ? 'Disable' : 'Enable'
      )
      .afterClosed()
      .pipe(
        switchMap((dialogClosed) => {
          if (dialogClosed)
            return this.service.changeUserAvailability(data.id, !data.isActive);
          else return of(false);
        })
      )
      .subscribe((deleteConfirmation) => {
        if (deleteConfirmation) {
          this.reload();
        }
      });

    this.subscriptions.push(deleteSuscription);
  }

  private reload() {
  this.isLoading = true;
  this.searchKey = this.searchControl.value?.toLowerCase() || '';
  this.gridApi.showLoadingOverlay();
  
  const params: any = {
    sortColumn: this.currentSortField.sortValue,
    sortOrder: this.currentIsAscending ? 'asc' : 'desc',
    page: this.currentPage + 1,
    pageSize: this.pageSize,
    searchKey: this.searchKey
  };
  
  if (!this.showAll) {
    params.isActive = true;
  }
  
  const subscription = this.service
    .get(params)
    .pipe(
      switchMap((response: any) => {
        if (!response.isError) {
          return of(response.data);
        } else {
          return of([]);
        }
      })
    )
    .subscribe(data => {
      this.gridApi.hideOverlay();
      this.rowData$ = data.items;
      this.totalCount = data.totalCount;
      this.isLoading = false;
      this.form.reset();
      this.drawer.close();
    });
  this.subscriptions.push(subscription);
}

onShowAllChange(): void {
  this.currentPage = 0;
  this.reload();
}
  handlePageEvent(event: PageEvent){
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload();
  }

  setSortField() {
    const { sortField, isAscending } = this.sortForm.value;
    this.currentIsAscending = isAscending;
    this.currentSortField = sortField;

    this.reload();
  }

  resetSortField() {
    this.sortForm.setValue({
      sortField: this.currentSortField,
      isAscending: this.currentIsAscending
    })
  }

  //----------GRID FUNCTIONS

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.reload();
    this.adjustColumnVisibility(this.gridApi);
  }

  private adjustColumnVisibility(gridApi: any) {
    let columns = undefined;

    if (!this.isLender() && this.userRole() !== Role.LenderReadOnly) {
      const actionCol = {
        field: 'action',
        minWidth: 80,
        maxWidth: 80,
        filter: false,
        sortable: false,
        cellRenderer: BtnCellRenderer,
        cellRendererParams: {
          onEdit: this.openRightSidebar.bind(this),
          onChangeAvailability: this.onChangeAvailability.bind(this),
        },
      };

      columns = [...this.columnDefs, actionCol];
    } else {
      columns = this.columnDefs;
    }

    gridApi.setColumnDefs(columns);
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach((s) => s.unsubscribe());
  }
}
